import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  LayoutDashboard, AlertTriangle, IndianRupee, Users, Bot, Handshake,
  RefreshCw, ArrowRightLeft, Clock, UserX, Phone, Warehouse,
  TrendingUp, TrendingDown, Award, Target, ChevronRight, Eye,
  Activity, CheckCircle, XCircle, Timer
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "../components/ui/table";
import Layout from "../components/Layout";
import LiveVisitAdoptionCard from "../components/LiveVisitAdoptionCard";
import { API, useProject } from "../App";
import { toast } from "sonner";
import { DateRangePicker, openLeadsTab } from "../components/DateRangePicker";
import { appendDateParams } from "../utils/dateFilters";
import { InfoTip } from "../components/InfoTip";
import { last30Days } from "../lib/dateDefaults";
import { useUrlFilters } from "../hooks/useUrlFilters";

const _DEF = last30Days();

const formatINR = (v) => {
  if (!v) return "₹0";
  const n = Number(v);
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)} L`;
  return `₹${n.toLocaleString("en-IN")}`;
};

const HEALTH_COLORS = {
  green: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", badge: "bg-emerald-100 text-emerald-700" },
  amber: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", badge: "bg-amber-100 text-amber-700" },
  red: { bg: "bg-red-50", border: "border-red-200", text: "text-red-700", badge: "bg-red-100 text-red-700" },
};

const STAGE_LABELS = {
  new: "New", contacted: "Contacted", qualified: "Qualified",
  site_visit_scheduled: "Site Visit Scheduled", site_visit: "Site Visit", revisit: "Revisit", negotiation_scheduled: "Negotiation Scheduled", negotiation: "Negotiation",
  booked: "Booked", re_engaged: "Re-Engaged", lost: "Lost",
};

const STAGE_COLORS = {
  new: "bg-blue-100 text-blue-700", contacted: "bg-indigo-100 text-indigo-700",
  qualified: "bg-violet-100 text-violet-700", site_visit_scheduled: "bg-orange-100 text-orange-700",
  site_visit: "bg-amber-100 text-amber-700", revisit: "bg-yellow-100 text-yellow-700",
  negotiation_scheduled: "bg-cyan-100 text-cyan-700", negotiation: "bg-teal-100 text-teal-700",
  booked: "bg-emerald-100 text-emerald-700", re_engaged: "bg-orange-100 text-orange-700", lost: "bg-red-100 text-red-700",
};

const ManagerDashboardPage = () => {
  const { selectedProjectId } = useProject();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignLead, setReassignLead] = useState(null);
  const [reassignTo, setReassignTo] = useState("");
  const [reassigning, setReassigning] = useState(false);
  const [urlFilters, setUrlFilters] = useUrlFilters({
    date_from: _DEF.from,
    date_to: _DEF.to,
    action_tab: "overdue",
  });
  const dateFrom = urlFilters.date_from;
  const setDateFrom = (v) => setUrlFilters({ date_from: v });
  const dateTo = urlFilters.date_to;
  const setDateTo = (v) => setUrlFilters({ date_to: v });
  const actionTab = urlFilters.action_tab;
  const setActionTab = (v) => setUrlFilters({ action_tab: v });

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const dp = new URLSearchParams();
      appendDateParams(dp, dateFrom, dateTo);
      if (selectedProjectId !== "all") dp.set("project_id", selectedProjectId);
      const qs = dp.toString();
      const res = await axios.get(`${API}/manager-dashboard${qs ? `?${qs}` : ""}`);
      setData(res.data);
    } catch (err) {
      toast.error("Failed to load manager dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDashboard(); }, [dateFrom, dateTo, selectedProjectId]);

  const handleReassign = async () => {
    if (!reassignLead || !reassignTo) return;
    setReassigning(true);
    try {
      await axios.post(`${API}/manager-dashboard/reassign`, {
        lead_id: reassignLead.lead_id,
        assigned_to: reassignTo,
      });
      toast.success("Lead reassigned successfully");
      setReassignOpen(false);
      setReassignLead(null);
      setReassignTo("");
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reassignment failed");
    } finally {
      setReassigning(false);
    }
  };

  const openReassign = (lead) => {
    setReassignLead(lead);
    setReassignTo("");
    setReassignOpen(true);
  };

  if (loading && !data) {
    return (
      <Layout>
        <div className="p-6 max-w-7xl mx-auto" data-testid="manager-dashboard-loading">
          <div className="flex items-center justify-center py-32">
            <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        </div>
      </Layout>
    );
  }

  if (!data) return null;

  const { overview, team_performance, project_health, action_center, available_reps } = data;

  return (
    <Layout>
      <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto space-y-6" data-testid="manager-dashboard-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']" data-testid="manager-dashboard-title">
              Manager Dashboard
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Multi-project command center — team performance, pipeline & actions
            </p>
          </div>
          <div className="flex items-center gap-2">
            <DateRangePicker dateFrom={dateFrom} dateTo={dateTo} onChange={(f, t) => { setDateFrom(f); setDateTo(t); }} />
            <Button
              variant="outline"
              size="sm"
              onClick={fetchDashboard}
              disabled={loading}
              data-testid="manager-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* ===== OVERVIEW STRIP ===== */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="overview-strip">
          <OverviewCard
            icon={<Clock className="w-5 h-5 text-amber-600" />}
            label="Pending Approvals"
            value={overview.pending_approvals}
            color="amber"
            testId="stat-pending-approvals"
            onClick={() => openLeadsTab({ stage: "new", date_from: dateFrom, date_to: dateTo })}
          />
          <OverviewCard
            icon={<AlertTriangle className="w-5 h-5 text-red-600" />}
            label="SLA Breaches"
            value={overview.sla_breaches}
            color="red"
            testId="stat-sla-breaches"
            onClick={() => openLeadsTab({ date_from: dateFrom, date_to: dateTo })}
          />
          <OverviewCard
            icon={<IndianRupee className="w-5 h-5 text-emerald-600" />}
            label="Pipeline Value"
            value={formatINR(overview.pipeline_value)}
            color="emerald"
            testId="stat-pipeline-value"
            onClick={() => openLeadsTab({ date_from: dateFrom, date_to: dateTo })}
          />
          <OverviewCard
            icon={<Handshake className="w-5 h-5 text-teal-600" />}
            label="Active Negotiations"
            value={overview.active_negotiations}
            color="teal"
            testId="stat-active-negotiations"
            onClick={() => openLeadsTab({ stage: "negotiation", date_from: dateFrom, date_to: dateTo })}
          />
          <OverviewCard
            icon={<Users className="w-5 h-5 text-blue-600" />}
            label="New Leads Today"
            value={overview.new_leads_today}
            color="blue"
            testId="stat-new-leads-today"
            onClick={() => openLeadsTab({ stage: "new", date_from: dateFrom, date_to: dateTo })}
          />
          <OverviewCard
            icon={<Bot className="w-5 h-5 text-violet-600" />}
            label="Chatbot Pending"
            value={overview.chatbot_pending}
            color="violet"
            testId="stat-chatbot-pending"
          />
        </div>

        {/* ===== TEAM PERFORMANCE + PROJECT HEALTH ===== */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Team Performance — 2 cols */}
          <div className="lg:col-span-2 space-y-4">
            <Card data-testid="team-performance-card">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
                    <Users className="w-5 h-5 text-slate-600" />
                    Team Performance
                    <Badge variant="secondary" className="ml-2">{team_performance.total_reps} reps</Badge>
                    <InfoTip text="Each rep's total leads, booked deals, conversion rate, and active pipeline. Click any row to see that rep's leads." />
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                {/* Top / Bottom Performers */}
                {team_performance.top_performers?.length > 0 && (
                  <div className="flex flex-wrap gap-3 mb-4">
                    {team_performance.top_performers.map((r, i) => (
                      <div key={r.user_id} className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 cursor-pointer hover:bg-emerald-100 transition-colors" data-testid={`top-performer-${i}`} onClick={() => openLeadsTab({ assigned_to: r.user_id, date_from: dateFrom, date_to: dateTo })}>
                        <Award className="w-4 h-4 text-emerald-600" />
                        <span className="text-sm font-medium text-emerald-800">{r.name}</span>
                        <span className="text-xs text-emerald-600">{r.conversion_rate}%</span>
                      </div>
                    ))}
                    {team_performance.bottom_performers?.length > 0 && team_performance.bottom_performers.map((r, i) => (
                      <div key={r.user_id} className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 cursor-pointer hover:bg-red-100 transition-colors" data-testid={`bottom-performer-${i}`} onClick={() => openLeadsTab({ assigned_to: r.user_id, date_from: dateFrom, date_to: dateTo })}>
                        <TrendingDown className="w-4 h-4 text-red-500" />
                        <span className="text-sm font-medium text-red-800">{r.name}</span>
                        <span className="text-xs text-red-600">{r.conversion_rate}%</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Rep Table */}
                <div className="overflow-x-auto rounded-lg border border-slate-200">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-xs">Rep</TableHead>
                        <TableHead className="text-xs text-center">Leads</TableHead>
                        <TableHead className="text-xs text-center">Active</TableHead>
                        <TableHead className="text-xs text-center">Booked</TableHead>
                        <TableHead className="text-xs text-center">Conv %</TableHead>
                        <TableHead className="text-xs text-center">Tasks</TableHead>
                        <TableHead className="text-xs text-center">Visits</TableHead>
                        <TableHead className="text-xs text-center">Activity</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {team_performance.reps.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center text-sm text-slate-400 py-8">
                            No team members found
                          </TableCell>
                        </TableRow>
                      ) : (
                        team_performance.reps.map((r) => (
                          <TableRow key={r.user_id} data-testid={`rep-row-${r.user_id}`} className="cursor-pointer hover:bg-slate-50 transition-colors" onClick={() => openLeadsTab({ assigned_to: r.user_id, date_from: dateFrom, date_to: dateTo })}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                                  {r.picture ? <img src={r.picture} alt="" className="w-full h-full object-cover" /> : <span className="text-xs font-medium">{r.name?.charAt(0)}</span>}
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-slate-900 truncate max-w-[120px]">{r.name}</p>
                                  <p className="text-xs text-slate-400 capitalize">{r.role?.replace(/_/g, " ")}</p>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-center text-sm">{r.total_leads}</TableCell>
                            <TableCell className="text-center text-sm">{r.active_leads}</TableCell>
                            <TableCell className="text-center text-sm font-medium text-emerald-700">{r.booked}</TableCell>
                            <TableCell className="text-center">
                              <span className={`text-sm font-semibold ${r.conversion_rate >= 10 ? "text-emerald-600" : r.conversion_rate >= 5 ? "text-amber-600" : "text-slate-600"}`}>
                                {r.conversion_rate}%
                              </span>
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="flex flex-col items-center">
                                <span className="text-sm">{r.tasks_completed}/{r.tasks_total}</span>
                                <Progress value={r.task_completion_pct} className="w-14 h-1 mt-1" />
                              </div>
                            </TableCell>
                            <TableCell className="text-center text-sm">{r.site_visits}</TableCell>
                            <TableCell className="text-center">
                              <span className={`text-sm ${r.interactions_this_week > 5 ? "text-emerald-600 font-medium" : "text-slate-500"}`}>
                                {r.interactions_this_week}/wk
                              </span>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Project Health — 1 col */}
          <div className="space-y-3" data-testid="project-health-section">
            <LiveVisitAdoptionCard />

            <h3 className="text-lg font-semibold font-['Manrope'] text-slate-900 flex items-center gap-2 pt-2">
              <Activity className="w-5 h-5 text-slate-600" />
              Project Health
              <InfoTip text="Lead funnel and pipeline value for each project. Green = healthy conversion, Yellow = needs attention, Red = critical. Click any card to see that project's leads." />
            </h3>
            {project_health.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-sm text-slate-400">
                  No projects in scope
                </CardContent>
              </Card>
            ) : (
              project_health.map((p) => {
                const hc = HEALTH_COLORS[p.health_label] || HEALTH_COLORS.amber;
                return (
                  <Card key={p.project_name} className={`${hc.bg} ${hc.border} border cursor-pointer hover:shadow-md transition-shadow`} data-testid={`project-health-${p.project_name}`} onClick={() => openLeadsTab({ project: p.project_name, date_from: dateFrom, date_to: dateTo })}>
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold text-sm text-slate-900 truncate max-w-[160px]">{p.project_name}</h4>
                        <Badge className={`${hc.badge} text-xs`}>{p.health_score}/100</Badge>
                      </div>

                      {/* Funnel mini */}
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(p.stages || {}).map(([stage, count]) => (
                          <span key={stage} className={`text-[10px] px-1.5 py-0.5 rounded ${STAGE_COLORS[stage] || "bg-slate-100 text-slate-600"}`}>
                            {STAGE_LABELS[stage] || stage}: {count}
                          </span>
                        ))}
                      </div>

                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-slate-500">Leads</span>
                          <p className="font-semibold text-slate-800">{p.total_leads}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Conv Rate</span>
                          <p className="font-semibold text-slate-800">{p.conversion_rate}%</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Pipeline</span>
                          <p className="font-semibold text-slate-800">{formatINR(p.pipeline_value)}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Negotiations</span>
                          <p className="font-semibold text-slate-800">{p.active_negotiations}</p>
                        </div>
                      </div>

                      {/* Inventory row */}
                      {p.inventory && p.inventory.total > 0 && (
                        <div className="flex items-center gap-1 text-[10px]">
                          <Warehouse className="w-3 h-3 text-slate-500" />
                          <span className="text-emerald-700">{p.inventory.available} avail</span>
                          <span className="text-slate-300">|</span>
                          <span className="text-amber-700">{p.inventory.blocked} blocked</span>
                          <span className="text-slate-300">|</span>
                          <span className="text-blue-700">{p.inventory.booked} booked</span>
                          <span className="text-slate-300">|</span>
                          <span className="text-slate-500">{p.inventory.sold} sold</span>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        </div>

        {/* ===== ACTION CENTER ===== */}
        <Card data-testid="action-center-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
              <Target className="w-5 h-5 text-red-500" />
              Action Center
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs value={actionTab} onValueChange={setActionTab}>
              <TabsList className="w-full h-auto flex overflow-x-auto gap-1 p-1" data-testid="action-center-tabs">
                <TabsTrigger value="overdue" className="text-xs whitespace-nowrap px-2 py-2 flex-shrink-0">
                  Overdue
                  {action_center.overdue_tasks?.length > 0 && (
                    <Badge variant="destructive" className="ml-1 h-4 px-1 text-[10px]">
                      {action_center.overdue_tasks.length}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="stale" className="text-xs whitespace-nowrap px-2 py-2 flex-shrink-0">
                  Stale
                  {action_center.stale_leads?.length > 0 && (
                    <Badge className="ml-1 h-4 px-1 text-[10px] bg-amber-100 text-amber-700">
                      {action_center.stale_leads.length}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="callbacks" className="text-xs whitespace-nowrap px-2 py-2 flex-shrink-0">
                  Callbacks
                  {action_center.callbacks?.length > 0 && (
                    <Badge className="ml-1 h-4 px-1 text-[10px] bg-blue-100 text-blue-700">
                      {action_center.callbacks.length}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="blocks" className="text-xs whitespace-nowrap px-2 py-2 flex-shrink-0">
                  Expiring
                  {action_center.expiring_blocks?.length > 0 && (
                    <Badge className="ml-1 h-4 px-1 text-[10px] bg-violet-100 text-violet-700">
                      {action_center.expiring_blocks.length}
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>

              {/* Overdue Tasks */}
              <TabsContent value="overdue" className="mt-3">
                {action_center.overdue_tasks?.length === 0 ? (
                  <EmptyState icon={<CheckCircle className="w-8 h-8 text-emerald-400" />} message="No overdue tasks" />
                ) : (
                  <div className="space-y-2">
                    {action_center.overdue_tasks.map((t) => (
                      <div key={t.task_id} className="flex items-center justify-between bg-red-50 border border-red-200 rounded-lg px-4 py-3" data-testid={`overdue-task-${t.task_id}`}>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">{t.title}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-slate-500">{t.assigned_name}</span>
                            <span className="text-xs text-red-600">Due: {new Date(t.due_date).toLocaleDateString("en-IN")}</span>
                            {t.priority && <Badge className="text-[10px] h-4 bg-red-100 text-red-700">{t.priority}</Badge>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Stale Leads */}
              <TabsContent value="stale" className="mt-3">
                {action_center.stale_leads?.length === 0 ? (
                  <EmptyState icon={<CheckCircle className="w-8 h-8 text-emerald-400" />} message="No stale leads" />
                ) : (
                  <div className="space-y-2">
                    {action_center.stale_leads.map((l) => (
                      <div key={l.lead_id} className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-4 py-3" data-testid={`stale-lead-${l.lead_id}`}>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">{l.name}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <Badge className={`text-[10px] h-4 ${STAGE_COLORS[l.stage] || "bg-slate-100 text-slate-600"}`}>{STAGE_LABELS[l.stage] || l.stage}</Badge>
                            <span className="text-xs text-slate-500">{l.assigned_name}</span>
                            {l.project_interest && <span className="text-xs text-slate-400">{l.project_interest}</span>}
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" className="text-xs flex-shrink-0" onClick={() => openReassign(l)} data-testid={`reassign-stale-${l.lead_id}`}>
                          <ArrowRightLeft className="w-3.5 h-3.5 mr-1" />
                          Reassign
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Callbacks */}
              <TabsContent value="callbacks" className="mt-3">
                {action_center.callbacks?.length === 0 ? (
                  <EmptyState icon={<Phone className="w-8 h-8 text-blue-400" />} message="No pending callbacks" />
                ) : (
                  <div className="space-y-2">
                    {action_center.callbacks.map((c) => (
                      <div key={c.task_id} className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-4 py-3" data-testid={`callback-${c.task_id}`}>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">{c.title}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-slate-500">{c.assigned_name}</span>
                            {c.due_date && <span className="text-xs text-blue-600">Due: {new Date(c.due_date).toLocaleDateString("en-IN")}</span>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Expiring Blocks */}
              <TabsContent value="blocks" className="mt-3">
                {action_center.expiring_blocks?.length === 0 ? (
                  <EmptyState icon={<Warehouse className="w-8 h-8 text-violet-400" />} message="No expiring blocks" />
                ) : (
                  <div className="space-y-2">
                    {action_center.expiring_blocks.map((b, i) => (
                      <div key={b.unit_id || i} className="flex items-center justify-between bg-violet-50 border border-violet-200 rounded-lg px-4 py-3" data-testid={`expiring-block-${i}`}>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900">{b.project_name} — {b.tower} {b.unit_number}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-slate-500">Blocked for: {b.blocked_for || "N/A"}</span>
                            <span className="text-xs text-violet-600">Since: {new Date(b.updated_at).toLocaleDateString("en-IN")}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* ===== REASSIGN DIALOG ===== */}
        <Dialog open={reassignOpen} onOpenChange={setReassignOpen}>
          <DialogContent data-testid="reassign-dialog">
            <DialogHeader>
              <DialogTitle className="font-['Manrope']">Reassign Lead</DialogTitle>
            </DialogHeader>
            {reassignLead && (
              <div className="space-y-4">
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-sm font-medium text-slate-900">{reassignLead.name}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge className={`text-[10px] h-4 ${STAGE_COLORS[reassignLead.stage] || "bg-slate-100 text-slate-600"}`}>
                      {STAGE_LABELS[reassignLead.stage] || reassignLead.stage}
                    </Badge>
                    <span className="text-xs text-slate-500">Current: {reassignLead.assigned_name}</span>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1.5">Assign to</label>
                  <Select value={reassignTo} onValueChange={setReassignTo}>
                    <SelectTrigger data-testid="reassign-select">
                      <SelectValue placeholder="Select a rep..." />
                    </SelectTrigger>
                    <SelectContent>
                      {(available_reps || []).filter(r => r.user_id !== reassignLead.assigned_to).map((r) => (
                        <SelectItem key={r.user_id} value={r.user_id}>
                          {r.name} ({r.active_leads} active leads)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setReassignOpen(false)}>Cancel</Button>
              <Button onClick={handleReassign} disabled={!reassignTo || reassigning} data-testid="confirm-reassign-btn">
                {reassigning ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRightLeft className="w-4 h-4 mr-2" />}
                Reassign
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

/* ----- Sub-components ----- */

const OverviewCard = ({ icon, label, value, color, testId, onClick }) => (
  <Card className={`relative overflow-hidden ${onClick ? "cursor-pointer hover:shadow-md transition-shadow" : ""}`} data-testid={testId} onClick={onClick}>
    <CardContent className="p-4">
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <span className="text-xs text-slate-500 font-medium">{label}</span>
      </div>
      <p className="text-xl font-bold text-slate-900 font-['Manrope']">{value}</p>
    </CardContent>
  </Card>
);

const EmptyState = ({ icon, message }) => (
  <div className="flex flex-col items-center justify-center py-8 text-center">
    {icon}
    <p className="text-sm text-slate-400 mt-2">{message}</p>
  </div>
);

export default ManagerDashboardPage;
